/**
 * `/findings` — the ledger, and `/findings/<key>` — the case file.
 *
 * Finding keys carry `/` and `:`. The link is built with `encodeURIComponent`
 * and the backend route is a wildcard, so `horeca/proxysql:conn_err` opens
 * rather than 404s. That pairing is asserted on both sides.
 *
 * The filters are a horizontal rail rather than a stacked card. Three wrapped
 * chip rows cost ~180 px at 390 px, which is a quarter of the viewport spent on
 * controls before the first finding is visible; a rail costs one row each and
 * still shows every facet count.
 */

import type { ReactNode } from 'react';

import { fullTime, relativeTime } from '../lib/format';
import { Card, Eyebrow, Ident, KeyValue, Placeholder, Skeleton, StatusPill } from '../components/primitives';
import { Icon } from '../components/icons';

export interface FindingView {
  key: string;
  title: string;
  status: string;
  severity: string;
  owner: string;
  firstSeen: string;
  lastSeen: string;
  issueRef: string | null;
  notes: string | null;
}

export interface FindingsData {
  generatedAt: string;
  total: number;
  filters: { status: string | null; severity: string | null; owner: string | null; q: string | null };
  findings: FindingView[];
  facets: {
    status: Record<string, number>;
    severity: Record<string, number>;
    owner: Record<string, number>;
  };
}

export interface CaseFileData {
  generatedAt: string;
  key: string;
  finding: FindingView | null;
  found: boolean;
  timeline: { kind: string; at: string; ref: string; summary: string; detail: Record<string, unknown> }[];
  counts: { runs: number; proposals: number; approvals: number; audit: number };
}

/*
 * The ledger's own vocabularies are NOT the signal vocabulary — `new`,
 * `escalated`, `known`, `resolved` are workflow states, not measurements. They
 * are mapped onto the signal statuses only to reuse the chip's tone and glyph;
 * that is a rendering convenience and is why `lib/coverage.ts` explicitly
 * refuses to count a `FindingView.status` as a measurement.
 */
const SEVERITY_STATUS: Record<string, string> = {
  critical: 'error',
  warning: 'unproven',
  info: 'empty',
};

/*
 * `new` is NOT an alarm. It is "nobody has looked at this yet", which is the
 * dotted container — the same thing `unproven` means one layer down. Only
 * `escalated` earns the red: something was looked at and pushed up. Mapping
 * `new` to red, as the previous round did, put six red chips on a ledger whose
 * worst item was one escalation, and then the escalation was invisible.
 */
const STATUS_STATUS: Record<string, string> = {
  new: 'unproven',
  escalated: 'error',
  known: 'empty',
  resolved: 'ok',
};

/** The scan rail. Only `critical` is coloured; the rest are ink. */
const SEVERITY_RAIL: Record<string, string> = {
  critical: 'bg-bad',
  warning: 'bg-ink-3',
  info: 'bg-ink-4/40',
};

export function FindingsPage({
  data,
  search,
  navigate,
}: {
  data: FindingsData | null;
  search: string;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) {
    return (
      <Skeleton label="the findings ledger" shapes={['h-28', 'h-64']} />
    );
  }

  const params = new URLSearchParams(search);
  const setFilter = (name: string, value: string | null): void => {
    const next = new URLSearchParams(search);
    if (value === null) next.delete(name);
    else next.set(name, value);
    const query = next.toString();
    navigate(`/findings${query === '' ? '' : `?${query}`}`);
  };

  const anyFilter =
    params.get('status') !== null ||
    params.get('severity') !== null ||
    params.get('owner') !== null ||
    params.get('q') !== null;

  return (
    <div className="space-y-3 md:space-y-4">
      <Card
        title="Filters"
        icon="search"
        subtitle="counts are over the whole ledger"
        right={
          anyFilter ? (
            <button
              type="button"
              onClick={() => {
                navigate('/findings');
              }}
              className="rounded-lg border border-line px-2.5 text-2xs text-ink-3 hover:border-line-strong hover:text-ink"
            >
              clear all
            </button>
          ) : undefined
        }
      >
        <div className="space-y-2.5">
          <FilterRail
            label="status"
            facets={data.facets.status}
            active={params.get('status')}
            onPick={(v) => {
              setFilter('status', v);
            }}
          />
          <FilterRail
            label="severity"
            facets={data.facets.severity}
            active={params.get('severity')}
            onPick={(v) => {
              setFilter('severity', v);
            }}
          />
          <FilterRail
            label="owner"
            facets={data.facets.owner}
            active={params.get('owner')}
            onPick={(v) => {
              setFilter('owner', v);
            }}
          />
          <div>
            <label htmlFor="finding-search" className="mb-1 block">
              <Eyebrow>search key, title and notes</Eyebrow>
            </label>
            <input
              id="finding-search"
              type="search"
              defaultValue={params.get('q') ?? ''}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  const value = (event.target as HTMLInputElement).value;
                  setFilter('q', value === '' ? null : value);
                }
              }}
              placeholder="press Enter to search"
              className="w-full rounded-lg border border-line bg-surface-2 px-3 text-sm text-ink placeholder:text-ink-3"
            />
          </div>
        </div>
      </Card>

      {data.findings.length === 0 ? (
        <Placeholder status="empty" title="no findings match">
          {data.total === 0 && Object.keys(data.facets.status).length === 0
            ? 'The ledger has no findings at all yet.'
            : 'Clear a filter above to widen the search.'}
        </Placeholder>
      ) : (
        <Card
          title={`${String(data.total)} matching ${data.total === 1 ? 'finding' : 'findings'}`}
          icon="findings"
          subtitle="tap one for its case file"
          padding="flush"
        >
          <ul className="divide-y divide-line border-t border-line">
            {data.findings.map((finding) => (
              <li key={finding.key}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/findings/${encodeURIComponent(finding.key)}`);
                  }}
                  className="flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2"
                >
                  {/* Severity as a rail: found by the eye before it is read. */}
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 w-0.5 shrink-0 self-stretch rounded-full ${
                      SEVERITY_RAIL[finding.severity] ?? 'bg-neutral'
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-start justify-between gap-2">
                      <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-ink">
                        {finding.title}
                      </span>
                      <span className="flex shrink-0 gap-1">
                        <StatusPill status={SEVERITY_STATUS[finding.severity] ?? 'unproven'} size="sm">
                          {finding.severity}
                        </StatusPill>
                        <StatusPill status={STATUS_STATUS[finding.status] ?? 'unproven'} size="sm">
                          {finding.status}
                        </StatusPill>
                      </span>
                    </span>
                    <Ident className="mt-1 block break-all text-[11px] leading-snug text-ink-3">
                      {finding.key}
                    </Ident>
                    <span className="num mt-1 block text-2xs text-ink-3">
                      {finding.owner} · first seen {relativeTime(finding.firstSeen)} · last seen{' '}
                      {relativeTime(finding.lastSeen)}
                      {finding.issueRef !== null && ` · ${finding.issueRef}`}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function FilterRail({
  label,
  facets,
  active,
  onPick,
}: {
  label: string;
  facets: Record<string, number>;
  active: string | null;
  onPick: (value: string | null) => void;
}): ReactNode {
  const entries = Object.entries(facets).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return null;
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div className="rail mt-1 flex gap-1.5 py-1">
        <Chip active={active === null} onClick={() => { onPick(null); }}>
          all
        </Chip>
        {entries.map(([value, count]) => (
          <Chip
            key={value}
            active={active === value}
            onClick={() => {
              onPick(value);
            }}
          >
            {value} <span className="num text-ink-3">{count}</span>
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 whitespace-nowrap rounded-lg border px-3 text-xs transition-colors ${
        active
          ? 'border-accent/45 bg-accent/10 font-medium text-ink'
          : 'border-line text-ink-3 hover:border-line-strong hover:text-ink-2'
      }`}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Case file
 * ------------------------------------------------------------------ */

/**
 * The timeline spine's dots. Ink, at three weights — a case file is a history,
 * and history is not an alarm. The KIND is written out beside every dot, so
 * the dot never has to carry it.
 */
const TIMELINE_TONE: Record<string, string> = {
  run: 'bg-ink',
  proposal: 'bg-ink-2',
  approval: 'bg-ink-2',
  audit: 'bg-ink-4',
};

export function CaseFilePage({
  data,
  navigate,
}: {
  data: CaseFileData | null;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) {
    return <Skeleton label="this finding" shapes={['h-64']} />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <button
        type="button"
        onClick={() => {
          navigate('/findings');
        }}
        className="inline-flex items-center gap-2 rounded-lg border border-line px-3 text-xs text-ink-3 transition-colors hover:border-line-strong hover:text-ink"
      >
        <Icon name="back" size={14} /> all findings
      </button>

      <Card
        title={data.finding?.title ?? data.key}
        subtitle={<Ident className="break-all">{data.key}</Ident>}
        right={
          data.finding === null ? (
            <StatusPill status="unproven">no ledger row</StatusPill>
          ) : (
            <div className="flex flex-wrap justify-end gap-1">
              <StatusPill status={SEVERITY_STATUS[data.finding.severity] ?? 'unproven'}>
                {data.finding.severity}
              </StatusPill>
              <StatusPill status={STATUS_STATUS[data.finding.status] ?? 'unproven'}>
                {data.finding.status}
              </StatusPill>
            </div>
          )
        }
      >
        {data.finding === null ? (
          <p className="text-xs leading-relaxed text-ink-3">
            Activity in the ledger, but no finding row. The timeline below is everything recorded
            against this key.
          </p>
        ) : (
          <dl className="divide-y divide-line">
            <KeyValue label="owner">{data.finding.owner}</KeyValue>
            <KeyValue label="first seen">
              <span className="num" title={fullTime(data.finding.firstSeen)}>
                {relativeTime(data.finding.firstSeen)}
              </span>
            </KeyValue>
            <KeyValue label="last seen">
              <span className="num" title={fullTime(data.finding.lastSeen)}>
                {relativeTime(data.finding.lastSeen)}
              </span>
            </KeyValue>
            <KeyValue label="issue">
              {data.finding.issueRef ?? <span className="text-ink-3">none linked</span>}
            </KeyValue>
          </dl>
        )}
        {data.finding?.notes != null && (
          <p className="mt-3 border-t border-line pt-3 text-sm leading-relaxed text-ink-2">
            {data.finding.notes}
          </p>
        )}
      </Card>

      <Card
        title="Case file"
        icon="findings"
        subtitle={`${String(data.counts.runs)} runs · ${String(data.counts.proposals)} proposals · ${String(data.counts.approvals)} approvals · ${String(data.counts.audit)} audit rows`}
      >
        {data.timeline.length === 0 ? (
          <Placeholder status="empty" title="nothing recorded against this key yet" />
        ) : (
          <ol className="relative space-y-3 pl-5">
            {/* The spine. Decorative: every entry is also dated in text. */}
            <span
              aria-hidden="true"
              className="absolute bottom-2 left-1.5 top-2 w-px bg-line"
            />
            {data.timeline.map((entry) => (
              <li key={`${entry.kind}-${entry.ref}-${entry.at}`} className="relative">
                <span
                  aria-hidden="true"
                  className={`absolute -left-[15px] top-1.5 h-2 w-2 rounded-full ring-3 ring-surface ${
                    TIMELINE_TONE[entry.kind] ?? 'bg-neutral'
                  }`}
                />
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="text-2xs font-semibold uppercase tracking-[0.08em] text-ink-3">
                    {entry.kind}
                  </span>
                  <span className="num text-2xs text-ink-3" title={fullTime(entry.at)}>
                    {relativeTime(entry.at)}
                  </span>
                </div>
                <p className="mt-0.5 text-sm leading-relaxed text-ink-2">{entry.summary}</p>
                {entry.kind === 'run' && (
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/runs/${String(entry.ref)}`);
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 text-2xs text-ink-3 transition-colors hover:border-line-strong hover:text-ink"
                  >
                    open the run
                    <Icon name="chevron" size={13} />
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
